import cls from "classnames";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import ErrorIcon from "images/errorIcon.svg?react";
import WarningIcon from "images/new_warning_icon.svg?react";
import Button from "components/ui/Button";

import styles from "./index.module.scss";

type Props = {
  children: ReactNode;
  /** What the message is: something that failed, or a consequence to weigh first. */
  tone?: "danger" | "warning";
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
};

/**
 * A failure that belongs to a form, a panel or a page, with an optional action to
 * try whatever failed again. A failure announces itself, so it is heard when it
 * appears; a warning is part of what the reader opened and is read with the rest.
 *
 * For an error that belongs to one form control, reach for `FieldError` instead.
 */
export default function ErrorBanner(props: Props) {
  const { t } = useTranslation();
  const warning = props.tone === "warning";
  const Icon = warning ? WarningIcon : ErrorIcon;

  return (
    <div
      role={warning ? undefined : "alert"}
      className={cls(styles.banner, warning && styles.warning, props.className)}
    >
      <Icon className={styles.icon} />
      <div className={styles.content}>
        <div className={styles.message}>{props.children}</div>
        {props.onRetry && (
          <Button variant="secondary" size="extra-small" onClick={props.onRetry}>
            {props.retryLabel ?? t("Common.actions.tryAgain")}
          </Button>
        )}
      </div>
    </div>
  );
}
